import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { HandCoins, Eye, Download, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDateTime = (s) => (s
  ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

const CompensationPage = ({ user, setUser }) => {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLetters = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/compensation/my`, { headers: { Authorization: `Bearer ${token}` } });
      setLetters(res.data || []);
    } catch (e) {
      toast.error('Failed to load compensation letters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLetters(); }, [fetchLetters]);

  const fetchBlob = async (id, inline = false) => {
    const token = localStorage.getItem('token');
    try {
      return await axios.get(`${API}/compensation/${id}/download${inline ? '?disposition=inline' : ''}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
    } catch (err) {
      let msg = 'Failed to fetch the PDF';
      try {
        if (err.response?.data instanceof Blob) {
          msg = JSON.parse(await err.response.data.text())?.detail || msg;
        } else {
          msg = err.response?.data?.detail || msg;
        }
      } catch { /* keep generic */ }
      toast.error(msg);
      return null;
    }
  };

  const handleView = async (letter) => {
    const res = await fetchBlob(letter.id, true);
    if (!res) return;
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
  };

  const handleDownload = async (letter) => {
    const res = await fetchBlob(letter.id);
    if (!res) return;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = letter.original_filename || `Compensation_Letter.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="compensation-page">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HandCoins className="w-5 h-5 text-emerald-600" /> Compensation Letters
            </CardTitle>
            <CardDescription>Compensation letters uploaded by HR/Admin. View or download your letters anytime.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : letters.length === 0 ? (
              <div className="text-center py-10 text-gray-500" data-testid="compensation-empty">
                <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p>No compensation letters yet.</p>
                <p className="text-sm">When HR uploads a letter for you, it will appear here and also arrive in your email.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Letter</TableHead>
                    <TableHead>Uploaded On</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {letters.map((l) => (
                    <TableRow key={l.id} data-testid={`compensation-row-${l.id}`}>
                      <TableCell className="font-medium">{l.original_filename}</TableCell>
                      <TableCell data-testid={`compensation-date-${l.id}`}>{fmtDateTime(l.uploaded_at)}</TableCell>
                      <TableCell>{l.uploaded_by_name || l.uploaded_by}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button size="sm" variant="outline" onClick={() => handleView(l)} data-testid={`compensation-view-${l.id}`}>
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button size="sm" onClick={() => handleDownload(l)} data-testid={`compensation-download-${l.id}`}>
                          <Download className="w-4 h-4 mr-1" /> Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CompensationPage;
